import useTranslation from '@app/hooks/use-translation';
import {
  ManualAndAutoFilledCriteriaObjectType,
  MonitoringRecordDataType,
  MonitoringRecordEditObjectType,
} from '@app/interfaces/qm-module/qm-manage/qm-monioring-tasks.type';
import { BoxSpoiler, Button, CircleButton, EasyModal, NavigationTabs, Select } from '@ui';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useGetUserInfoQuery } from '@app/store/api/get-user-info.api';

import {
  useApproveOperatorMutation,
  useChangeAppealStatusMutation,
  useFillAppealMutation,
} from '@app/store/api/qm-module/qm-appeal/qm-appeal.api';

import {
  QmAppealFillingObjectType,
  QmAppealFullObjectType,
  QmAppealResolution,
  QmAppealStatuses,
} from '@app/interfaces/qm-module/qm-appellations/qm-appellations.type';

import {
  useEditRecordMonitoringDataMutation,
  useLazyGetRecordMonitoringDataExportQuery,
} from '@app/store/api/qm-module/qm-manage/qm-manage-monitoring-tasks.api';

import { NavigationTabsItem } from '@app/components/ui/navigation-tabs/navigation-tabs';

import AccordionItemUI from '@app/components/ui/Accordion/AccordionItem';

import useOnClickOutside from '@app/hooks/use-onclick-outside';

import { toast } from 'react-toastify';

import { navigationRoutes } from '@app/utils/navigation-routes';

import fileDownload from 'js-file-download';

import { useAudioContext } from '../record-provider';

import CriteriaListMarkup from './criteria-list-markup';
import HistoryComponent from './history-component';
import { DisplayScoreComponent } from './display-score';

type RecordMonitoringPropsType = {
  recordMonitoringData: MonitoringRecordDataType | undefined;
  recordMfId?: string;
  appealObject: QmAppealFullObjectType | undefined;
};

export type RecordEditTempType = {
  result?: {
    id?: number;
  };
  comment?: {
    value: string | number;
  };
};

const RecordMonitoring: FC<RecordMonitoringPropsType> = (props) => {
  const { recordMonitoringData, recordMfId, appealObject } = props;
  const { appealResolveMode } = useAudioContext();
  const { data: userInfo } = useGetUserInfoQuery();
  const [editAppealStatus] = useChangeAppealStatusMutation();
  const editMode =
    appealObject && appealResolveMode
      ? appealObject.status === 'IN_WORK'
      : !recordMonitoringData?.data.confirmed &&
        userInfo?.qm_role !== 'operator' &&
        userInfo?.qm_role !== 'supervisor';

  const [appealResolution, changeResolution] = useState<
    QmAppealFullObjectType['resolution'] | undefined
  >();
  const [appealSendMode, setAppealSendModeState] = useState(false);
  const [historyModal, setHistoryModalState] = useState(false);
  const [criteriaToAppeal, setCriteriaToAppeal] = useState<Record<string, string | number>>({});
  const combinedCriteria = [
    ...(recordMonitoringData?.criteria?.blocks.map((block) => block.criteria).flat(2) || []),
    ...(recordMonitoringData?.criteria?.plain || []),
  ] as ManualAndAutoFilledCriteriaObjectType[];
  const [activeCriteriaFlagForHistory, setCriteriaFlagForHistory] = useState<string>(
    combinedCriteria?.[0]?.id,
  );
  const defaultCriteriaEditObj: Record<string, RecordEditTempType> = combinedCriteria.reduce<
    Record<string, RecordEditTempType>
  >((acc, cur) => {
    acc[cur.id] = { result: { id: cur.settings.result.id } };
    return acc;
  }, {});

  const scalesColorsMap: Record<string, { [key: number]: string }> = combinedCriteria.reduce<
    Record<string, { [key: number]: string }>
  >((acc, cur) => {
    const a = cur.settings.rating_scale?.items.reduce((obj, scale) => {
      obj[scale.id] = scale.color;
      return obj;
    }, {});
    acc[cur.id] = { ...acc, ...a };
    return acc;
  }, {});

  const [criteriaEditObject, setCriteriaEditObject] =
    useState<Record<string, RecordEditTempType>>(defaultCriteriaEditObj);

  // api
  const [editMonitoring] = useEditRecordMonitoringDataMutation();
  const [fillAppeal] = useFillAppealMutation();
  const [approveOperator] = useApproveOperatorMutation();
  const [exportRecordMonitoringData] = useLazyGetRecordMonitoringDataExportQuery();
  // api
  async function onExportMonitoring() {
    if (!recordMfId) return;
    const file = await exportRecordMonitoringData({ id: recordMfId });
    file?.data && fileDownload(file.data, `${recordMonitoringData?.name}.xlsx`);
  }

  const { t } = useTranslation('components.audio.monitoring');

  function handleChangeAppealStatus(status: QmAppealStatuses) {
    if (!appealObject) return;
    if (status === 'ACCEPTED') {
      handleSendMonitoringChanges();
    }
    editAppealStatus({
      body: { status, resolution: appealResolution },
      params: { appellation_id: appealObject?.appellation_id },
    })
      .unwrap()
      .then(() => toast.success(t('toasts.success_appeal_status')))
      .catch(() => toast.error(t('toasts.err_appeal_status')));
  }

  function handleSendAppeal() {
    if (!recordMfId) return;
    const formattedData: QmAppealFillingObjectType = {
      mfs_id: recordMfId,
      criteria: Object.entries(criteriaToAppeal).map(([id, comment]) => ({
        id,
        comment: comment ? comment : undefined,
      })),
    };

    fillAppeal(formattedData)
      .unwrap()
      .then(() => {
        toast.success(t('toasts.success_appeal'));
      })
      .catch(() => {
        toast.error(t('toasts.err_appeal'));
      })
      .finally(() => {
        setCriteriaToAppeal({});
        setAppealSendModeState(false);
      });
  }
  function handleIncludeCriteriaToAppeal(id: string) {
    if (typeof criteriaToAppeal[id] === 'string') {
      setCriteriaToAppeal((prev) => {
        const temp = { ...prev };
        delete temp[id];
        return temp;
      });
      return;
    }
    setCriteriaToAppeal((prev) => ({ ...prev, [id]: '' }));
  }

  function handleChangeCriteriaComment(value: string | number, id: string) {
    if (typeof criteriaToAppeal[id] !== 'string') return;
    setCriteriaToAppeal((prev) => ({ ...prev, [id]: value }));
  }

  function handleSendMonitoringChanges() {
    if (!recordMfId) return;
    const hasNullValue = Object.values(criteriaEditObject).some(({ result }) => !result?.id);
    if (hasNullValue) {
      toast.error(t('toasts.nullable_val_err'));
      return;
    }
    const body: MonitoringRecordEditObjectType = {
      criteria: Object.entries(criteriaEditObject).map(([id, body]) => ({ ...body, id })),
    } as MonitoringRecordEditObjectType;
    editMonitoring({ params: { id: recordMfId || '' }, body });
    setCriteriaEditObject((prev) => {
      const length = Object.keys(prev);
      const temp = { ...prev };
      for (let i = 0; i < length.length; i++) {
        delete temp?.[length[i]]?.comment;
      }
      return temp;
    });
  }

  const historyCriteriaTabs: NavigationTabsItem[] = combinedCriteria.map((criteria) => ({
    index: criteria.id,
    label: criteria.name,
  }));

  const [activeAccordionItem, setActiveAccordionItem] = React.useState<string[]>([]);

  function handleAccordionClick({ id }: { id: string }) {
    const formattedAccordionArray = activeAccordionItem.includes(id)
      ? activeAccordionItem.filter((accordionItem) => accordionItem !== id)
      : [...activeAccordionItem, id];
    setActiveAccordionItem(formattedAccordionArray);
  }

  const checkAllBlockCriteria = useCallback(() => {
    const suspect = recordMonitoringData?.criteria.blocks;
    const openGroupsIds: string[] = [];
    if (!suspect) return;

    outerLoop: for (let i = 0; i < suspect?.length; i++) {
      const criteriaList = suspect[i].criteria;

      for (let j = 0; j < criteriaList.length; j++) {
        const currentCriteria = criteriaList[j];
        // is auto check
        if (
          currentCriteria.type === 'AUTO' &&
          (currentCriteria.settings.result?.score || 0) < currentCriteria.evaluation.score.max_calc
        ) {
          openGroupsIds.push(suspect[i].id);
          continue outerLoop;
        }
        // is auto check

        // appeal check condition
        if (appealObject?.criteria.map((i) => i.id).includes(currentCriteria.id)) {
          openGroupsIds.push(suspect[i].id);
          continue outerLoop;
        }
        // appeal check condition

        // comments check condition
        if (
          currentCriteria.comments.history.length &&
          currentCriteria.comments.history.length > 0
        ) {
          openGroupsIds.push(suspect[i].id);
          continue outerLoop;
        }
        // comments check condition

        // criteria check was set lower rating that was
        const criteriaHistory = currentCriteria?.settings?.result?.history;
        if (criteriaHistory?.length) {
          const lastUpdate = criteriaHistory[criteriaHistory.length - 1];
          const rating_before = currentCriteria.settings.rating_scale?.items.find(
            (item) => item.id === lastUpdate.value_before,
          )?.rating;
          const rating_after = currentCriteria.settings.rating_scale?.items.find(
            (item) => item.id === lastUpdate.value_set,
          )?.rating;

          if (typeof rating_after === 'number' && typeof rating_before === 'number') {
            if (rating_after < rating_before) {
              openGroupsIds.push(suspect[i].id);
            }
          }
        }
        // criteria check was set lower rating that was
      }
    }
    setActiveAccordionItem(openGroupsIds);
  }, [appealObject, recordMonitoringData?.criteria.blocks]);

  useEffect(() => checkAllBlockCriteria(), [checkAllBlockCriteria]);

  return (
    <>
      <BoxSpoiler
        defaultStatus
        title={
          <div className="flex items-center justify-between w-full">
            <div>
              {t('title')} {recordMonitoringData?.name}
            </div>
            <div className="text-[16px] flex items-center gap-[5px]">
              <span className="flex items-center gap-[10px]">
                {t('final_score')}:
                <DisplayScoreComponent
                  fullFilled
                  score={recordMonitoringData?.data.score}
                  color={
                    recordMonitoringData?.conditional_formatting.find(
                      (form) =>
                        form.value?.[0] <= (recordMonitoringData?.data?.score || 0) &&
                        (recordMonitoringData?.data?.score || 0) <= form.value?.[1],
                    )?.color
                  }
                  maxScore={recordMonitoringData?.evaluation.score.max_calc}
                />
              </span>
              <CircleButton
                hintTitle={t('hints.monitoring_download')}
                size={17}
                onClick={onExportMonitoring}
                icon="DownloadCloudIcon"
                className="text-action"
              />
              <CircleButton
                hintTitle={t('hints.change_history')}
                hintPlace="left"
                disable={userInfo?.qm_role !== 'manager' && userInfo?.qm_role !== 'controller'}
                onClick={() => setHistoryModalState(true)}
                size={16}
                icon="BookIcon"
                className="text-action"
              />
            </div>
          </div>
        }
      >
        <div className="">
          <div className="flex gap-[10px] items-center  justify-end">
            {appealObject && appealResolveMode
              ? appealObject.status === 'SENT' &&
                userInfo?.qm_role === 'manager' && (
                  <Button
                    label={t('consider')}
                    onClick={() => handleChangeAppealStatus('IN_WORK')}
                    fill="linked"
                    disabled={appealObject.status !== 'SENT'}
                  />
                )
              : userInfo?.qm_role !== 'operator' &&
                userInfo?.qm_role !== 'supervisor' && (
                  <Button
                    // disabled={
                    //   (userInfo?.qm_role !== 'controller' || userInfo?.qm_role !== 'manager') &&
                    //   JSON.stringify(defaultCriteriaEditObj) === JSON.stringify(criteriaEditObject)
                    // }
                    label={
                      JSON.stringify(defaultCriteriaEditObj) === JSON.stringify(criteriaEditObject)
                        ? t('save_and_approve_controller')
                        : t('save_and_approve')
                    }
                    fill="linked"
                    onClick={handleSendMonitoringChanges}
                  />
                )}
          </div>

          <div className="pb-[15px]">
            <div className="">
              {recordMonitoringData?.criteria?.blocks?.map((block) => {
                const isOpened = activeAccordionItem.includes(block.id);
                return (
                  <AccordionItemUI
                    variant="filled"
                    classname="!h-[50px]"
                    key={block.id + block.name}
                    title={
                      <div className=" w-full items-center grid-cols-[1fr_127px] grid gap-2 pr-[26px]">
                        <div className={`flex items-baseline gap-[5px]`}>
                          {block.name}
                          <Hint content={block.description} />
                        </div>
                        <div className="content-end justify-self-start">
                          <DisplayScoreComponent
                            maxScore={block.evaluation.score.max_calc}
                            score={block.data.score}
                            color={
                              block.conditional_formatting.find(
                                (form) =>
                                  form.value[0] <= (block.data.score || 0) &&
                                  (block.data.score || 0) <= form.value[1],
                              )?.color
                            }
                          />
                        </div>
                      </div>
                    }
                    id={block.id}
                    active={isOpened}
                    clickBtnChild={handleAccordionClick}
                  >
                    <CriteriaListMarkup
                      handleIncludeCriteriaToAppeal={handleIncludeCriteriaToAppeal}
                      handleChangeCriteriaComment={handleChangeCriteriaComment}
                      criteriaToAppeal={criteriaToAppeal}
                      appealSendMode={appealSendMode}
                      editMode={editMode}
                      criteria={block.criteria}
                      scalesColorsMap={scalesColorsMap}
                      criteriaEditObject={criteriaEditObject}
                      recordMonitoringData={recordMonitoringData}
                      setCriteriaEditObject={setCriteriaEditObject}
                      appealObject={appealObject}
                      appealResolveMode={appealResolveMode}
                    />
                  </AccordionItemUI>
                );
              })}
            </div>
            <CriteriaListMarkup
              handleIncludeCriteriaToAppeal={handleIncludeCriteriaToAppeal}
              criteriaToAppeal={criteriaToAppeal}
              handleChangeCriteriaComment={handleChangeCriteriaComment}
              appealSendMode={appealSendMode}
              recordMonitoringData={recordMonitoringData}
              appealResolveMode={appealResolveMode}
              editMode={editMode}
              scalesColorsMap={scalesColorsMap}
              criteria={recordMonitoringData?.criteria?.plain}
              criteriaEditObject={criteriaEditObject}
              appealObject={appealObject}
              setCriteriaEditObject={setCriteriaEditObject}
            />
          </div>
        </div>
        {appealObject?.status === 'IN_WORK' && appealResolveMode && (
          <div className="p-[0px_26px_26px_26px]">
            <div className="w-[250px]">
              <Select
                placeholder={t('resolution')}
                options={[
                  { value: 'OPERATOR_ERROR', title: t('resolution_err_operator') },
                  { value: 'CONTROLLER_ERROR', title: t('resolution_err_controller') },
                  { value: 'NO_ERROR', title: t('resolution_err_none') },
                ]}
                defaultValue={appealResolution}
                onChange={(val) => changeResolution(val as QmAppealResolution)}
              />
            </div>
          </div>
        )}

        <div className="flex w-full justify-end">
          {appealObject && appealResolveMode ? (
            userInfo?.qm_role === 'supervisor' ? (
              <>
                <Button
                  label={t('refuse')}
                  onClick={() => handleChangeAppealStatus('CLOSED')}
                  fill="linked"
                  variant="danger"
                  disabled={appealObject.status !== 'CREATED'}
                />
                <Button
                  label={t('send_appeal')}
                  onClick={() => handleChangeAppealStatus('SENT')}
                  fill="linked"
                  disabled={appealObject.status !== 'CREATED'}
                />
              </>
            ) : userInfo?.qm_role === 'manager' ? (
              <>
                <Button
                  label={t('exit')}
                  fill="linked"
                  link={'/' + navigationRoutes.qm_module_appeal}
                />
                <Button
                  label={t('cancel')}
                  onClick={() => handleChangeAppealStatus('DECLINED')}
                  fill="linked"
                  variant="danger"
                  disabled={appealObject.status !== 'IN_WORK'}
                />
                <Button
                  label={t('approve_and_save_appeal')}
                  onClick={() => handleChangeAppealStatus('ACCEPTED')}
                  fill="linked"
                  disabled={appealObject.status !== 'IN_WORK' || !appealResolution}
                />
              </>
            ) : null
          ) : (
            <>
              {userInfo?.qm_role === 'operator' && (
                <Button
                  label={t('operator_approve')}
                  onClick={() => approveOperator({ id: recordMfId || '' })}
                  fill="linked"
                  disabled={
                    recordMonitoringData?.data.complete ? recordMonitoringData.data.confirmed : true
                  }
                />
              )}
              {userInfo?.qm_role === 'operator' && (
                <>
                  <Button
                    onClick={() =>
                      appealSendMode ? handleSendAppeal() : setAppealSendModeState(true)
                    }
                    label={t('approve_appeal')}
                    fill="linked"
                    disabled={
                      !recordMonitoringData?.appeal_available ||
                      recordMonitoringData?.data.confirmed ||
                      userInfo.qm_role !== 'operator'
                    }
                  />
                  {appealSendMode && (
                    <Button
                      onClick={() => {
                        setAppealSendModeState(false);
                        setCriteriaToAppeal({});
                      }}
                      label={t('cancel_appeal')}
                      fill="linked"
                      variant="danger"
                      disabled={
                        !recordMonitoringData?.appeal_available || userInfo.qm_role !== 'operator'
                      }
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </BoxSpoiler>

      <EasyModal
        withoutFooter
        variant="medium484"
        show={historyModal}
        onClose={() => setHistoryModalState(false)}
      >
        <div className="min-h-[300px]">
          <div className="bg-basic_app_bg p-1 rounded-defaultR">
            <NavigationTabs
              overflow
              items={historyCriteriaTabs}
              onChange={({ index }) => {
                setCriteriaFlagForHistory(index.toString());
              }}
              conditionalIsActive={activeCriteriaFlagForHistory}
            />
          </div>
          <HistoryComponent
            className="max-h-[350px]"
            history={
              combinedCriteria.find(({ id }) => id === activeCriteriaFlagForHistory)?.settings
                .result.history || []
            }
          />
        </div>
      </EasyModal>
      {/* HISTORY MODAL */}
    </>
  );
};

export default RecordMonitoring;

export const Hint = ({ content, size = 13 }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null!);
  useOnClickOutside(ref, () => setOpen(false));
  return (
    <div ref={ref} className="relative z-[99] danger">
      <CircleButton
        onClick={() => setOpen((prev) => !prev)}
        icon="HelpCircleIcon"
        className="text-action"
        size={size}
      />
      <div
        className={`
    w-[250px] max-h-[150px] overflow-y-auto overflow-x-hidden
    break-words whitespace-pre-wrap
    before:absolute before:border-b-[7px] before:border-l-[5px] before:border-r-[5px]
    before:border-bg_4 before:border-r-transparent before:border-l-transparent
    before:top-[-7px] before:left-[7px]
    absolute top-[25px] transition-all bg-bg_4 p-[6px_12px] rounded-defaultR
    ${open ? 'visible' : 'invisible'}
  `}
      >
        <span>{content}</span>
      </div>
    </div>
  );
};
